import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Download, X, Share, Plus } from "lucide-react";

const DISMISS_KEY = "pwa-install-dismissed";

const isStandalone = () =>
  window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;

const isIos = () =>
  /iphone|ipad|ipod/i.test(window.navigator.userAgent) && !window.MSStream;

const isMobile = () => window.matchMedia("(max-width: 767px)").matches;

export default function InstallPWA() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [showIosHelp, setShowIosHelp] = useState(false);
  const [mobile, setMobile] = useState(false);

  useEffect(() => {
    if (isStandalone() || localStorage.getItem(DISMISS_KEY) === "1") return;
    setMobile(isMobile());

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);

    // Mobile: gently slide up a one-time hint shortly after arrival.
    // (iOS never fires beforeinstallprompt, so we show it there regardless.)
    let timer;
    if (isMobile()) {
      timer = setTimeout(() => setVisible(true), 3500);
    }

    const onInstalled = () => { setVisible(false); localStorage.setItem(DISMISS_KEY, "1"); };
    window.addEventListener("appinstalled", onInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
      if (timer) clearTimeout(timer);
    };
  }, []);

  const install = async () => {
    if (deferred) {
      deferred.prompt();
      const { outcome } = await deferred.userChoice;
      if (outcome === "accepted") { setVisible(false); localStorage.setItem(DISMISS_KEY, "1"); }
      setDeferred(null);
    } else if (isIos()) {
      setShowIosHelp(true);
    }
  };

  const dismiss = () => {
    setVisible(false);
    setShowIosHelp(false);
    localStorage.setItem(DISMISS_KEY, "1");
  };

  return (
    <>
      <AnimatePresence>
        {visible && mobile && (
          <motion.div
            key="pwa-mobile"
            initial={{ y: 120, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 120, opacity: 0 }}
            transition={{ type: "spring", stiffness: 260, damping: 26 }}
            className="fixed bottom-4 inset-x-4 z-[55]"
            data-testid="install-pwa-banner"
          >
            <div className="flex items-center gap-3 bg-[#121212] border border-primary/40 shadow-[0_10px_40px_rgba(0,0,0,0.8)] p-3">
              <img src="/icons/icon-192.png" alt="7 Star Limo" className="w-11 h-11 shrink-0 border border-white/10" />
              <div className="min-w-0 flex-1">
                <div className="text-sm text-foreground font-heading">Install the 7 Star Limo app</div>
                <div className="text-xs text-muted-foreground">Book rides in two taps — add it to your home screen.</div>
              </div>
              <button
                type="button"
                data-testid="install-pwa-add"
                onClick={install}
                className="shrink-0 bg-primary text-primary-foreground text-xs uppercase tracking-wide font-semibold px-4 py-2.5 hover:bg-primary/90 transition-colors"
              >
                Add
              </button>
              <button
                type="button"
                data-testid="install-pwa-dismiss"
                onClick={dismiss}
                aria-label="Dismiss"
                className="shrink-0 text-muted-foreground hover:text-foreground p-1"
              >
                <X size={16} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Desktop: subtle floating install button (only when Chrome offers it) */}
      {visible && !mobile && (
        <div className="fixed bottom-6 left-6 z-50" data-testid="install-pwa">
          <div className="flex items-center gap-2 bg-[#121212] border border-primary/40 shadow-[0_8px_30px_rgba(0,0,0,0.7)] pl-3 pr-2 py-2">
            <button type="button" data-testid="install-pwa-btn" onClick={install}
              className="flex items-center gap-2 text-sm text-primary hover:text-primary/80 transition-colors">
              <Download size={16} />
              <span className="uppercase tracking-wide">Install App</span>
            </button>
            <button type="button" data-testid="install-pwa-dismiss-desktop" onClick={dismiss}
              aria-label="Dismiss install prompt" className="text-muted-foreground hover:text-foreground p-1">
              <X size={15} />
            </button>
          </div>
        </div>
      )}

      {showIosHelp && (
        <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/70 px-4" data-testid="install-ios-help" onClick={dismiss}>
          <div className="bg-[#121212] border border-white/10 max-w-sm w-full p-6 mb-6 sm:mb-0" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-heading text-primary">Add to Home Screen</h3>
              <button onClick={dismiss} aria-label="Close" className="text-muted-foreground hover:text-foreground"><X size={18} /></button>
            </div>
            <p className="text-sm text-muted-foreground mb-4">Install 7 Star Limo on your iPhone in two taps:</p>
            <ol className="space-y-3 text-sm text-foreground">
              <li className="flex items-center gap-2">
                <span className="text-primary font-heading">1.</span>
                Tap the <Share size={15} className="text-primary inline" /> <span className="text-primary">Share</span> button in Safari
              </li>
              <li className="flex items-center gap-2">
                <span className="text-primary font-heading">2.</span>
                Choose <Plus size={15} className="text-primary inline" /> <span className="text-primary">Add to Home Screen</span>
              </li>
            </ol>
          </div>
        </div>
      )}
    </>
  );
}
